/**
 * Centralized Error Messages - Spark OS
 * 
 * All user-facing Hebrew error messages in one place.
 * Benefits: consistent UX, easier i18n, single source of truth.
 */

export const ERRORS = {
    // ========================================
    // Speech Recognition
    // ========================================
    SPEECH: {
        NO_SPEECH: 'לא זוהה דיבור. ודא שהמיקרופון פעיל ונסה שוב.',
        NOT_ALLOWED: 'נדרשת הרשאה למיקרופון. אשר בהגדרות הדפדפן.',
        NETWORK: 'בעיית חיבור. ודא שהאינטרנט פעיל ונסה שוב.',
        GENERIC: 'לא הצלחנו לזהות דיבור. נסה שוב.',
        PARSE_FAILED: 'לא הצלחנו לעבד את הטקסט. נסה שוב.',
        UNSUPPORTED: 'הדפדפן הנוכחי לא תומך בזיהוי דיבור.',
    },

    // ========================================
    // Authentication
    // ========================================
    AUTH: {
        GOOGLE_FAILED: 'לא הצלחנו להתחבר ל-Google. בדוק את החיבור ונסה שוב.',
        GOOGLE_CONNECT: 'החיבור ל-Google נכשל. נסה שוב.',
        LOGOUT_FAILED: 'לא הצלחנו להתנתק. נסה שוב.',
        GENERIC: 'משהו השתבש בהתחברות. נסה שוב.',
    },

    // ========================================
    // Google Services
    // ========================================
    GOOGLE: {
        CALENDAR: {
            LOAD_EVENTS: 'לא ניתן לטעון אירועים כרגע. נסה שוב בעוד רגע.',
            CREATE_EVENT: 'לא הצלחנו ליצור את האירוע. נסה שנית.',
            UPDATE_EVENT: 'לא הצלחנו לעדכן את האירוע. נסה שנית.',
            DELETE_EVENT: 'לא הצלחנו למחוק את האירוע. נסה שנית.',
            GET_EVENT: 'לא הצלחנו לטעון את האירוע. נסה שנית.',
            TIME_BLOCK: 'לא הצלחנו לחסום את הזמן. ודא שאתה מחובר ל-Google Calendar.',
        },
        DRIVE: {
            UPLOAD_BACKUP: 'לא הצלחנו להעלות את הגיבוי. נסה שוב.',
            DOWNLOAD_BACKUP: 'לא הצלחנו להוריד את הגיבוי. נסה שוב.',
        },
        SYNC: 'הסנכרון נתקע. אנחנו מנסים שוב.',
        DISCONNECT: 'לא הצלחנו להתנתק. נסה שוב.',
    },

    // ========================================
    // Data Operations (CRUD)
    // ========================================
    DATA: {
        CREATE_ITEM: 'לא הצלחנו ליצור את הפריט. נסה שנית.',
        UPDATE_ITEM: 'לא הצלחנו לעדכן את הפריט. נסה שנית.',
        DELETE_ITEM: 'לא הצלחנו למחוק. נסה שנית.',
        SAVE_ITEM: 'לא הצלחנו לשמור. נסה שנית.',
        ADD_TO_LIBRARY: 'לא הצלחנו להוסיף לספרייה. נסה שנית.',
        CREATE_ROADMAP: 'לא הצלחנו ליצור את מפת הדרכים. נסה שנית.',
        SAVE_NOTE: 'לא הצלחנו לשמור את ההערה. נסה שנית.',
        SAVE_TEMPLATE: 'לא הצלחנו לשמור את התבנית. נסה שנית.',
        CREATE_VAULT: 'לא הצלחנו ליצור את הכספת. נסה שנית.',
        IMPORT_FILE: 'לא הצלחנו לייבא את הקובץ. ודא שהקובץ תקין והסיסמה נכונה.',
        ADD_FEED: 'לא הצלחנו להוסיף את הפיד. נסה שנית.',
    },

    // ========================================
    // AI / Analysis
    // ========================================
    AI: {
        ASSISTANT_FAILED: 'השירות לא זמין כרגע. נסה שוב מאוחר יותר.',
        BRIEFING_FAILED: 'לא הצלחנו ליצור את התדריך. נסה שוב.',
        SUMMARY_FAILED: 'לא הצלחנו ליצור סיכום. נסה שוב.',
        SEARCH_FAILED: 'החיפוש נכשל. נסה שוב.',
        TOPIC_SUGGEST_FAILED: 'לא הצלחנו להציע נושאים. נסה שוב.',
        SUMMARIZE_FAILED: 'לא הצלחנו לסכם. נסה שוב.',
        MESSAGE_SEND_FAILED: 'לא הצלחנו לשלוח את ההודעה. נסה שוב.',
        PARSE_TEXT: 'לא הצלחנו לעבד את הטקסט. נסה שוב.',
    },

    // ========================================
    // Feed
    // ========================================
    FEED: {
        REFRESH_FAILED: 'לא הצלחנו לרענן את הפידים. נסה שוב.',
    },

    // ========================================
    // Investments
    // ========================================
    INVESTMENTS: {
        LOAD_MARKET: 'לא ניתן לטעון נתוני שוק כרגע. נסה שוב בעוד רגע.',
        ADD_ASSET: 'לא הצלחנו להוסיף את הנכס. נסה שנית.',
    },

    // ========================================
    // Generic
    // ========================================
    GENERIC: 'משהו השתבש. נסה שוב.',
} as const;

/**
 * Type-safe error key lookup
 */
export type ErrorCategory = keyof typeof ERRORS;
